/* Enhance the current homepage after native or asynchronous app rendering.
   The unenhanced page remains fully usable. */
(() => {
  'use strict';
  let dispose = () => {};
  let mountedHome = null, mountedTrack = null, contentObserver;
  function mount() {
    const home = document.querySelector('.home-experience');
    const shapeTrack = home?.querySelector('.home-shape-window') || null;
    if (home === mountedHome && shapeTrack === mountedTrack) return;
    dispose();
    mountedHome = home; mountedTrack = shapeTrack;
    if (!home) { dispose = () => {}; return; }
    const signalController = new AbortController(), {signal} = signalController;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let revealObserver, shapeObserver, resizeObserver, frame = 0, resumeTimer = 0;
    let outside = false, hovered = false, interacting = false, touching = false, lastTime = 0;
    let position = 0, direction = 1, edgeUntil = 0;
    function stopShapes() { cancelAnimationFrame(frame); frame = 0; lastTime = 0; }
    function canMove() {
      return shapeTrack && !signal.aborted && !reduced.matches && !document.hidden && !outside &&
        !hovered && !interacting && !touching && !shapeTrack.contains(document.activeElement) &&
        shapeTrack.scrollWidth > shapeTrack.clientWidth + 1;
    }
    function tick(time) {
      frame = 0;
      if (!canMove()) { lastTime = 0; return; }
      const elapsed = lastTime ? Math.min(time - lastTime, 64) : 0;
      lastTime = time;
      if (elapsed > 0 && time >= edgeUntil) {
        const max = shapeTrack.scrollWidth - shapeTrack.clientWidth;
        position = Math.max(0, Math.min(max, position + direction * elapsed * .024));
        shapeTrack.scrollLeft = position;
        if (position >= max || position <= 0) {
          direction = position >= max ? -1 : 1;
          edgeUntil = time + 1200;
        }
      }
      frame = requestAnimationFrame(tick);
    }
    function syncShapes() {
      stopShapes();
      if (!canMove()) return;
      position = shapeTrack.scrollLeft;
      frame = requestAnimationFrame(tick);
    }
    function manualShapes() {
      interacting = true; stopShapes(); clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { interacting = false; syncShapes(); }, 4500);
    }
    if (shapeTrack) {
      const rect = shapeTrack.getBoundingClientRect();
      outside = rect.bottom <= 0 || rect.top >= innerHeight;
      shapeTrack.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { hovered = true; syncShapes(); }
      }, {signal});
      shapeTrack.addEventListener('pointerleave', () => { hovered = false; syncShapes(); }, {signal});
      for (const type of ['pointerdown', 'pointerup', 'pointercancel', 'wheel', 'touchstart', 'touchend'])
        shapeTrack.addEventListener(type, manualShapes, {signal, passive:true});
      shapeTrack.addEventListener('pointerdown', () => { touching = true; stopShapes(); }, {signal, passive:true});
      for (const type of ['pointerup', 'pointercancel'])
        window.addEventListener(type, () => { if (touching) { touching = false; manualShapes(); } }, {signal, passive:true});
      shapeTrack.addEventListener('focusin', stopShapes, {signal});
      shapeTrack.addEventListener('focusout', () => queueMicrotask(syncShapes), {signal});
      document.addEventListener('visibilitychange', syncShapes, {signal});
      reduced.addEventListener('change', syncShapes, {signal});
      if (typeof IntersectionObserver !== 'undefined') {
        shapeObserver = new IntersectionObserver(entries => {
          outside = !entries[0].isIntersecting; syncShapes();
        }, {threshold:0});
        shapeObserver.observe(shapeTrack);
      }
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncShapes); resizeObserver.observe(shapeTrack);
      }
      syncShapes();
    }
    // Keep native swipe, keyboard navigation and every real shape link available.
    for (const track of home.querySelectorAll('.home-shape-window, .home-category-rail')) {
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const left = event.key === 'Home' ? 0 : event.key === 'End' ? track.scrollWidth - track.clientWidth :
          track.scrollLeft + (event.key === 'ArrowRight' ? 1 : -1) * track.clientWidth * .8;
        track.scrollTo({left, behavior: reduced.matches ? 'instant' : 'smooth'});
      }, {signal});
      track.addEventListener('focusin', event => {
        if (event.target === track || !event.target.matches('a')) return;
        event.target.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'instant'});
      }, {signal});
    }
    function resetReveals() {
      revealObserver?.disconnect();
      const sections = [...home.querySelectorAll('[data-home-reveal]')];
      home.classList.remove('home-reveal-ready');
      sections.forEach(section => section.classList.remove('is-pending'));
      if (reduced.matches || typeof IntersectionObserver === 'undefined') return;
      revealObserver = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) {
          entry.target.classList.remove('is-pending');
          revealObserver.unobserve(entry.target);
        }
      }, {threshold: .05, rootMargin: '0px 0px -20px 0px'});
      sections.forEach(section => {
        if (section.getBoundingClientRect().top > innerHeight * .9) {
          section.classList.add('is-pending');
          revealObserver.observe(section);
        }
      });
      home.classList.add('home-reveal-ready');
    }
    resetReveals();
    reduced.addEventListener('change', resetReveals, {signal});
    dispose = () => {
      signalController.abort(); revealObserver?.disconnect(); shapeObserver?.disconnect(); resizeObserver?.disconnect();
      stopShapes(); clearTimeout(resumeTimer);
      home.classList.remove('home-reveal-ready');
      home.querySelectorAll('.is-pending').forEach(section => section.classList.remove('is-pending'));
    };
  }
  function watchContent() {
    const main = document.getElementById('main');
    if (main && typeof MutationObserver !== 'undefined') {
      contentObserver ||= new MutationObserver(mount);
      contentObserver.observe(main, {childList:true, subtree:true});
    }
    mount();
  }
  // Clean-URL navigation and catalog readiness replace the homepage without
  // a hashchange. Remount only when its actual root or shape track changes.
  window.addEventListener('hashchange', mount);
  window.addEventListener('pagehide', () => {
    contentObserver?.disconnect(); dispose();
    mountedHome = null; mountedTrack = null;
  });
  window.addEventListener('pageshow', event => { if (event.persisted) watchContent(); });
  watchContent();
})();

